import { useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';

import AnimatedDigitsLabel from '../../../../components/AnimatedDigitsLabel';
import { usePrevious } from '../../../../hooks';
import { __ } from '../../../../i18n/l10n';
import GRProgressBarSvgAsComponent from '../../assets/svg/GRProgressBarSvgAsComponent';
import useGRHomeContext from '../../hooks/useGRHomeContext';
import useGRRoundContext from '../../hooks/useGRRoundContext';

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  &.gr-progress-bar-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-progress-bar-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }
`;

const LabelBubble = styled.div`
  position: relative;
  text-align: center;
  width: 100%;
  transform: translateY(0.5em) rotate(-4deg);
  font-size: 2em;
  margin-bottom: 0.2em;
`;

const LabelBubbleBackground = styled.div`
  -webkit-text-stroke: 10px #752917;
  position: absolute;
  z-index: -1;
  text-align: center;
  width: 100%;
`;

const LabelWrapper = styled.div`
  /* -webkit-text-stroke: 2px #752917; */
`;

const Label = styled(AnimatedDigitsLabel)`
  display: inline;
`;

const Svg = styled(GRProgressBarSvgAsComponent)``;

type GRProgressBarProps = {
  className?: string;
  points: number;
  maxPoints: number;
};

const formatter = (x: number) => `${Math.floor(x)}`;

const GRProgressBar = ({ className = '', points, maxPoints }: GRProgressBarProps) => {
  const prevPoints = usePrevious(points);
  const { fullScreenNotifShown } = useGRHomeContext();
  const { isWon } = useGRRoundContext();

  const LabelContent = useCallback(
    () => (
      <LabelWrapper>
        <Label
          current={prevPoints ?? points ?? 0}
          next={points}
          formatFn={formatter}
          lastFormatFn={formatter}
          timeout={1000}
        />
        /{maxPoints} {__('!short:Points')}
      </LabelWrapper>
    ),
    [prevPoints, points, maxPoints],
  );

  return maxPoints ? (
    <CSSTransition
      in={!fullScreenNotifShown && !isWon}
      classNames="gr-progress-bar-appearance-transition"
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <Wrapper className={className}>
        <LabelBubble>
          <LabelBubbleBackground>
            <LabelContent />
          </LabelBubbleBackground>
          <LabelContent />
        </LabelBubble>

        <Svg progressRatio={points / maxPoints} transitionDuration={1000} />
      </Wrapper>
    </CSSTransition>
  ) : null;
};

export default GRProgressBar;
